/**
 * Mux URL utilities for generating video and thumbnail URLs from playback IDs
 */

/**
 * Generate Mux thumbnail URL from playback ID
 * @see https://docs.mux.com/guides/get-images-from-a-video
 */
export function getMuxThumbnailUrl(playbackId: string, options?: {
	width?: number;
	height?: number;
	time?: number;
	fitMode?: 'preserve' | 'stretch' | 'crop' | 'smartcrop' | 'pad';
}): string {
	const params = new URLSearchParams();
	if (options?.width) params.set('width', String(options.width));
	if (options?.height) params.set('height', String(options.height));
	if (options?.time) params.set('time', String(options.time));
	if (options?.fitMode) params.set('fit_mode', options.fitMode);

	const queryString = params.toString();
	return `https://image.mux.com/${playbackId}/thumbnail.jpg${queryString ? `?${queryString}` : ''}`;
}

/**
 * Generate Mux video streaming URL from playback ID
 * Uses MP4 format for broad browser compatibility
 * @see https://docs.mux.com/guides/play-your-videos
 */
export function getMuxVideoUrl(playbackId: string, quality: 'low' | 'medium' | 'high' = 'high'): string {
	return `https://stream.mux.com/${playbackId}/${quality}.mp4`;
}

/**
 * Generate Mux HLS streaming URL from playback ID
 * Better for adaptive bitrate streaming but requires HLS.js for non-Safari browsers
 */
export function getMuxHlsUrl(playbackId: string): string {
	return `https://stream.mux.com/${playbackId}.m3u8`;
}

/**
 * Generate animated GIF preview from playback ID
 */
export function getMuxGifUrl(playbackId: string, options?: {
	width?: number;
	start?: number;
	end?: number;
	fps?: number;
}): string {
	const params = new URLSearchParams();
	if (options?.width) params.set('width', String(options.width));
	if (options?.start) params.set('start', String(options.start));
	if (options?.end) params.set('end', String(options.end));
	if (options?.fps) params.set('fps', String(options.fps));

	const queryString = params.toString();
	return `https://image.mux.com/${playbackId}/animated.gif${queryString ? `?${queryString}` : ''}`;
}
